import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiJson } from '../api/client';

export interface ActivePersona {
  id: string;
  name: string;
  full_name: string | null;
  description: string | null;
  voice: string;
  mood: string | null;
  greeting_template: string;
  enabled_skills: string[];
  priority_skills: string[];
  updated_at: number;
}

export interface InitResponse {
  user: { email: string };
  apiKeyConfigured: boolean;
  activePersona: ActivePersona | null;
  settings: Record<string, string>;
}

interface PersonaListResponse {
  personas: Array<{ id: string }>;
}

interface SeedResponse {
  seeded: number;
  existing?: number;
}

export function useInit() {
  const [init, setInit] = useState<InitResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // If persona list is empty, try to seed first.
      try {
        const listRes = await apiGet<PersonaListResponse>('/api/personas');
        if (!listRes.personas || listRes.personas.length === 0) {
          try {
            await apiJson<SeedResponse>('POST', '/api/seed/personas');
          } catch (seedErr) {
            // Seeding is best-effort; surface but don't block.
            console.warn('Persona seed failed:', seedErr);
          }
        }
      } catch (listErr) {
        console.warn('Persona list fetch failed:', listErr);
      }

      const data = await apiGet<InitResponse>('/api/init');
      setInit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { init, error, loading, refresh };
}
